import React, { FC } from 'react';
import { defaultsDeep } from 'lodash';
import { getAnchorRowStyle } from 'framework/ui/field';
import { useLocation, useParams } from 'react-router-dom';
import { EditableListDataGridLayout, EDITABLE_LIST_FIELD } from 'framework/list';

import {
  AmountSensitiveField,
  FactDateField,
  PlannedDateField,
  StatusField,
  MilestoneNameField,
  MilestoneActionField,
  PreviewBillingRequestLinkField,
} from '../fields';
import { ScheduleItemStatus } from '../../billingTypes';

const MilestoneBillingListGrid: FC<{ isSensitiveDataIsShown?: boolean }> = ({ isSensitiveDataIsShown }) => {
  const { hash } = useLocation();
  const { id } = useParams();

  return (
    <EditableListDataGridLayout
      rerenderTableCellsDeps={[isSensitiveDataIsShown]}
      rowProps={data => {
        const color =
          data.status === ScheduleItemStatus.APPROVED ||
          (data.status === ScheduleItemStatus.SYSTEM_PENDING && data.relatedItem)
            ? 'text.secondary'
            : 'inherit';
        return defaultsDeep(getAnchorRowStyle(data, hash), {
          sx: {
            padding: 0,
            px: 2,
            alignItems: 'center',
            py: 2,
            color: color,
          },
        });
      }}
      getTableCells={() =>
        Promise.resolve([
          {
            type: EDITABLE_LIST_FIELD,
            source: 'position',
            isEditor: false,
            component: PreviewBillingRequestLinkField,
            componentProps: {
              billingScheduleIdSource: 'billingScheduleId',
              billingRequestIdSource: 'billingRequestId',
              id,
              statusSource: 'status',
            },
            gridCellProps: { size: 0.5 },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'milestoneName',
            isEditor: false,
            component: MilestoneNameField,
            componentProps: { label: '', aliasSource: 'alias', changeRequestAliasSource: 'changeRequestAlias' },
            gridCellProps: { size: 3 },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'sensitiveData',
            isEditor: false,
            component: AmountSensitiveField,
            componentProps: { label: '', showContent: isSensitiveDataIsShown, tooltipText: 'Planned Amount' },
            gridCellProps: { size: 1.5 },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'amount',
            isEditor: false,
            component: AmountSensitiveField,
            componentProps: {
              label: '',
              showContent: isSensitiveDataIsShown,
              currencySource: 'currency',
              tooltipText: 'Actual Amount',
            },
            gridCellProps: { size: 1.5 },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'invoiceDate',
            isEditor: false,
            component: PlannedDateField,
            componentProps: { label: '' },
            gridCellProps: { size: 1.5 },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'billingRequestDate',
            isEditor: false,
            component: FactDateField,
            componentProps: { plannedDateSource: 'invoiceDate', isLinkedCRSource: 'isLinkedCR' },
            gridCellProps: { size: 1.5 },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'status',
            isEditor: false,
            component: StatusField,
            componentProps: { parentAliasSource: 'parentAlias' },
            gridCellProps: { size: 1, textAlign: 'end', sx: { '.MuiFormControl-root': { mb: 0 } } },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'status',
            isEditor: false,
            component: MilestoneActionField,
            componentProps: { id },
            gridCellProps: { size: 1, textAlign: 'end', sx: { '.MuiFormControl-root': { mb: 0 } } },
          },
        ])
      }
    />
  );
};

export default MilestoneBillingListGrid;
